import Header from "@/components/header";
import useIsSmall from "@/hooks/useIsSmall";
import { clubApi } from "@/services/axios";
import { Alert, Badge, Button, Modal, SegmentedControl, Skeleton, Text } from "@mantine/core";
import { IconChevronLeft } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import moment from "moment-jalaali";
import { useMemo, useState } from "react";
import classes from "./padixSettlements.module.css";

type Payout = { rrn: string; settleDate: string; amount: number; bookingCount: number };
type Summary = { settledAmount: number; settlementCount: number; pendingAmount: number; pendingBookings: number; settlements: Payout[] };
type Booking = { id: number; creationTime: string; customerName: string; paidAmount: number };

const fa = (n?: number) => (n ?? 0).toLocaleString("fa-IR");
const rial = (n?: number) => `${fa(n)} ریال`;
const jDate = (iso: string) => moment(iso).format("jYYYY/jMM/jDD");
const RANGES = [
  { value: "30", label: "۳۰ روز" },
  { value: "90", label: "۹۰ روز" },
  { value: "365", label: "یک سال" },
  { value: "all", label: "همه" },
];

// «تسویه‌های پدیکس»: what Padix collected online (deposits, online bookings) and paid into this
// account. The salon sees its own; a chair renter only theirs.
export default function PadixSettlements() {
  const isSmall = useIsSmall();
  const [range, setRange] = useState("90");
  const [open, setOpen] = useState<Payout | null>(null);
  const from = useMemo(() => (range === "all" ? undefined : moment().subtract(Number(range), "days").startOf("day").toISOString()), [range]);
  const query = useQuery({
    queryKey: ["padix-settlements", range],
    queryFn: async () => (await clubApi.instance.get<Summary>("/api/PadixSettlement", { params: { fromDate: from } })).data,
    retry: false,
  });
  const data = query.data;

  return (
    <>
      {isSmall && <Header showBack title="تسویه‌های پدیکس" />}
      <main className={classes.page}>
        <div className={classes.head}>
          <div>
            <h1>تسویه‌های پدیکس</h1>
            <p>پرداخت‌های آنلاین مشتری‌ها (بیعانه و رزرو آنلاین) را پدیکس دریافت و به حساب شما واریز می‌کند.</p>
          </div>
          <SegmentedControl size="xs" value={range} onChange={setRange} data={RANGES} />
        </div>

        {query.isLoading ? <Skeleton height={260} radius={14} /> : query.isError ? (
          <Alert color="red">{(query.error as { response?: { data?: { message?: string } } })?.response?.data?.message || "دریافت تسویه‌ها انجام نشد."}<Button variant="subtle" onClick={() => query.refetch()}>تلاش دوباره</Button></Alert>
        ) : data && (
          <>
            <div className={classes.kpis}>
              <div className={classes.kpi}><span>واریز شده به حساب شما</span><b>{rial(data.settledAmount)}</b><small>{fa(data.settlementCount)} واریز در این بازه</small></div>
              <div className={classes.kpi} data-tone="pending"><span>در انتظار تسویه</span><b>{rial(data.pendingAmount)}</b><small>برآورد · {fa(data.pendingBookings)} نوبت پرداخت‌شده</small></div>
            </div>
            <section className={classes.panel}>
              <h2>واریزها</h2>
              {!data.settlements.length ? <div className={classes.empty}>در این بازه واریزی ثبت نشده است.</div> : (
                <div className={classes.list}>
                  {data.settlements.map((s) => (
                    <button type="button" key={`${s.rrn}-${s.settleDate}`} className={classes.row} onClick={() => setOpen(s)}>
                      <div><strong>{rial(s.amount)}</strong><small>{jDate(s.settleDate)} · {fa(s.bookingCount)} نوبت</small></div>
                      <Badge variant="light" color="gray" className={classes.rrn}>پیگیری {s.rrn}</Badge>
                      <IconChevronLeft size={18} color="#9a96a1" />
                    </button>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </main>
      {open && <PayoutBookings payout={open} onClose={() => setOpen(null)} small={isSmall} />}
    </>
  );
}

function PayoutBookings({ payout, onClose, small }: { payout: Payout; onClose: () => void; small: boolean }) {
  const query = useQuery({
    queryKey: ["padix-settlement-bookings", payout.rrn, payout.settleDate],
    queryFn: async () => (await clubApi.instance.get<Booking[]>("/api/PadixSettlement/Bookings", { params: { rrn: payout.rrn, settleDate: payout.settleDate } })).data,
  });
  return (
    <Modal opened onClose={onClose} title={`واریز ${jDate(payout.settleDate)}`} {...(small ? { fullScreen: true, radius: 0 } : { centered: true, size: "lg" })}>
      <Text size="sm" mb="sm">مبلغ واریز: <b>{rial(payout.amount)}</b> · شماره پیگیری <span dir="ltr">{payout.rrn}</span></Text>
      {query.isLoading ? <Skeleton height={140} /> : query.isError ? <Alert color="red">دریافت نوبت‌ها انجام نشد.</Alert> : (
        <div className={classes.list}>
          {(query.data ?? []).map((b) => (
            <div key={b.id} className={classes.row} data-static>
              <div><strong>{b.customerName || "مشتری"}</strong><small>{jDate(b.creationTime)} · نوبت {fa(b.id)}</small></div>
              <span className={classes.amount}>{rial(b.paidAmount)}</span>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
